// Detects image data embedded in JSON values (e.g. the Word Cloud Image module's
// {"image_base64": "...", "mime_type": "image/png"}) so viewers can show a picture
// instead of a long base64 string. SVG is never accepted: it can carry script.

const RASTER_MIME_RE = /^image\/(?:png|jpe?g|gif|webp)$/i;
const DATA_IMAGE_RE = /^data:(image\/(?:png|jpe?g|gif|webp));base64,([A-Za-z0-9+/=\s]+)$/i;
const BASE64_RE = /^[A-Za-z0-9+/=\s]+$/;
const MIME_KEYS = ['mime_type', 'mimeType', 'content_type', 'contentType', 'media_type'];
// Keys that suggest a raw base64 value is a picture.
// These rules are mirrored in backend dep/utils/node_samples.py; keep them in sync.
const IMAGE_KEY_RE = /(image|img|png|jpe?g|photo|picture|thumbnail|wordcloud|word_cloud|chart|plot|logo)/i;
// Base64 of the PNG, JPEG, GIF and WebP magic numbers.
const MAGIC = [['iVBORw0KGgo', 'image/png'], ['/9j/', 'image/jpeg'], ['R0lGOD', 'image/gif'], ['UklGR', 'image/webp']];
const MIN_BASE64_CHARS = 32;

const base64Bytes = (b64) => {
  const clean = b64.replace(/\s/g, '');
  const pad = clean.endsWith('==') ? 2 : clean.endsWith('=') ? 1 : 0;
  return Math.max(0, Math.floor((clean.length * 3) / 4) - pad);
};

const siblingMime = (siblings) => {
  if (!siblings || typeof siblings !== 'object') return undefined;
  const key = MIME_KEYS.find((k) => typeof siblings[k] === 'string');
  return key ? siblings[key].trim() : undefined;
};

/**
 * Returns { src, mime, bytes } when `value` (found under `key`, next to `siblings`)
 * is a raster image, otherwise null. Accepted:
 * - data:image/png|jpeg|gif|webp;base64,… URIs, under any key;
 * - raw base64 with a PNG/JPEG/GIF/WebP signature next to an image/* mime_type
 *   (or content_type) sibling;
 * - raw base64 with a PNG/JPEG/GIF/WebP signature under an image-like key
 *   (image, png, wordcloud, thumbnail, …) when no mime sibling says otherwise.
 */
export const detectImage = (key, value, siblings) => {
  if (typeof value !== 'string') return null;
  const mimeHint = siblingMime(siblings);
  if (value.slice(0, 5).toLowerCase() === 'data:') {
    const m = DATA_IMAGE_RE.exec(value);
    if (!m) return null;
    return { src: value, mime: m[1].toLowerCase(), bytes: base64Bytes(m[2]) };
  }
  if (value.length < MIN_BASE64_CHARS || !BASE64_RE.test(value)) return null;
  // Real image bytes are required either way, so e.g. a hex checksum next to
  // content_type image/png isn't shown as a broken image.
  const magic = MAGIC.find(([prefix]) => value.startsWith(prefix));
  if (!magic) return null;
  let mime;
  if (mimeHint !== undefined) {
    if (!RASTER_MIME_RE.test(mimeHint)) return null; // a mime sibling decides, even when wrong
    mime = mimeHint.toLowerCase();
  } else {
    if (typeof key !== 'string' || !IMAGE_KEY_RE.test(key)) return null;
    [, mime] = magic;
  }
  return { src: `data:${mime};base64,${value.replace(/\s/g, '')}`, mime, bytes: base64Bytes(value) };
};

export const formatImageSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** Placeholder text shown instead of the base64 payload, e.g. "[image, 312 KB]". */
export const imageLabel = (image) => `[image, ${formatImageSize(image.bytes)}]`;
